/**
 *  @class
 *  @function RecentlyViewedPopup
 */
if (!customElements.get('recently-viewed-popup')) {
  class RecentlyViewedPopup extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.button = this.querySelector('.recently-viewed-button');
      this.popup = this.querySelector('.recently-viewed-popup');
      this.overlay = this.querySelector('.recently-viewed-popup__overlay');
      this.closeButton = this.querySelector('.recently-viewed-popup__close');
      this.content = this.querySelector('.recently-viewed-popup__content');
      this.container = this.querySelector('.recently-viewed-popup__container');
      this.url = `${theme.routes.search_url}?view=recently-viewed&type=product&q=`;
      this.maxProducts = 12;
    }

    connectedCallback() {
      // Initialize recently viewed IDs
      const recentIds = window.localStorage.getItem('recently-viewed');
      if (recentIds) {
        window.recentlyViewedIds = JSON.parse(recentIds);
      } else {
        window.recentlyViewedIds = [];
      }

      // Set up event listeners
      if (this.button) {
        this.button.addEventListener('click', (e) => {
          e.preventDefault();
          this.isOpen ? this.close() : this.open();
        });
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.close();
        });
      }

      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => {
          this.close();
        });
      }

      // Close on ESC key
      document.addEventListener('keyup', (e) => {
        if (e.code === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Hide/show button based on cart drawer state
      const updateButtonVisibility = () => {
        if (!this.button || this.isOpen) return; // Don't hide if popup is open
        
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && !cartDrawer.hasAttribute('inert')) {
          // Cart drawer is open, hide button
          this.button.style.display = 'none';
        } else {
          // Cart drawer is closed, show button if there are items
          const count = this.button.getAttribute('data-count');
          if (count !== '0') {
            this.button.style.display = '';
          }
        }
      };

      // Listen for cart drawer open event
      document.addEventListener('cart-drawer:open', () => {
        if (this.button && !this.isOpen) {
          this.button.style.display = 'none';
        }
      });

      // Check cart drawer state periodically
      setInterval(updateButtonVisibility, 200);

      // Initial check
      setTimeout(updateButtonVisibility, 100);

      // Update badge count
      this.updateBadge();
      
      // Listen for recently viewed updates
      window.addEventListener('storage', () => {
        this.updateBadge();
      });

      // Periodically check for updates (in case localStorage changed in same tab)
      setInterval(() => {
        this.updateBadge();
      }, 1000);
    }

    updateBadge() {
      const recentIds = window.localStorage.getItem('recently-viewed');
      let count = 0;
      if (recentIds) {
        const ids = JSON.parse(recentIds);
        count = ids.length;
      }
      
      const badge = this.button?.querySelector('.recently-viewed-button__badge');
      if (badge && this.button) {
        if (count > 0) {
          badge.textContent = count > 99 ? '99+' : count;
          badge.style.display = 'flex';
          this.button.removeAttribute('data-count');
        } else {
          badge.style.display = 'none';
          this.button.setAttribute('data-count', '0');
        }
      }
    }

    open() {
      if (!this.popup) return;
      
      this.popup.removeAttribute('inert');
      this.popup.focus();
      this.isOpen = true;
      
      // Hide floating button when popup is open
      if (this.button) {
        this.button.style.display = 'none';
      }
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Hide Shopify chat widget
      document.body.classList.add('open-cc');
      
      // Fetch products
      this.fetchProducts();
    }

    close() {
      if (!this.popup) return;
      
      this.popup.setAttribute('inert', '');
      this.isOpen = false;
      
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Show Shopify chat widget
      document.body.classList.remove('open-cc');
      
      // Show button again if cart drawer is not open
      setTimeout(() => {
        if (this.button && !this.isOpen) {
          const cartDrawer = document.querySelector('cart-drawer');
          const count = this.button.getAttribute('data-count');
          if ((!cartDrawer || cartDrawer.hasAttribute('inert')) && count !== '0') {
            this.button.style.display = '';
          }
        }
      }, 100);
    }

    buildUrl() {
      const recentIds = window.localStorage.getItem('recently-viewed');
      if (!recentIds) {
        return null;
      }

      const productHandles = JSON.parse(recentIds).slice(0, this.maxProducts);
      if (productHandles.length === 0) {
        return null;
      }

      const products = productHandles.map(handle => `handle:${handle}`).join(' OR ');
      return this.url + encodeURIComponent(products);
    }

    fetchProducts() {
      const url = this.buildUrl();
      if (!url) {
        this.showEmpty();
        return;
      }

      this.showLoading();

      fetch(url)
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const products = html.querySelector('.products');

          if (products && products.innerHTML.trim().length) {
            this.content.innerHTML = `<div class="recently-viewed-popup__products">${products.innerHTML}</div>`;
            
            // Initialize lazy loading for images
            if (typeof lazySizes !== 'undefined') {
              this.content.querySelectorAll('.lazyload').forEach((image) => {
                lazySizes.loader.unveil(image);
              });
            }
          } else {
            this.showEmpty();
          }
        })
        .catch(e => {
          console.error('Error fetching recently viewed products:', e);
          this.showEmpty();
        });
    }

    showLoading() {
      if (!this.content) return;
      this.content.innerHTML = `
        <div class="recently-viewed-popup__loading">
          <span>Loading...</span>
        </div>
      `;
    }

    showEmpty() {
      if (!this.content) return;
      this.content.innerHTML = `
        <div class="recently-viewed-popup__empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
          <p class="recently-viewed-popup__empty-text">You haven't viewed any products yet</p>
        </div>
      `;
    }
  }

  customElements.define('recently-viewed-popup', RecentlyViewedPopup);
}
